import * as React from "react";
import {cx}       from "emotion";
import ReactTooltip from 'react-tooltip';

import "./office-selector.scss";

import {faSearch}        from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {
    ComponentLink,
    Link,
    makeChain,
    valueHelper as value,
}                       from "@Common/react/cst2";
import {UseState}       from "@Common/react/use-state";
import {Types}          from "@Common/types";
import {SliderCheckbox} from "@Common/ui-components/slider-checkbox/slider-checkbox";

import {Tooltip}        from "@Xpresso/common/tool-tip";
import {consumeContext} from "@Xpresso/loaders/consume-context";
import {
    OfficeFilterBar,
    createIdFromOfficeName,
    navigateToSearchStart
}                       from "@Xpresso/routes/account/admin/office-management/office-management-route/office-filter-bar";

export const OfficeSelector = ({handleResponse, offices, submitting=null, newBranches=false}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("apis"))
    .then("searchState", (_, next:ComponentLink<typeof UseState<string>>) => UseState({
        _key: "searchState",
        defaultValue: "",
        next,
    }))
    .then("showingInactive", (_, next:ComponentLink<typeof UseState<boolean>>) => UseState({next, initValue: false}))
    .then("filteredOffices", ({searchState, showingInactive}, next:Link<JSX.Element, Types.Apis.Payloads.Next.Authenticate.Data.Branch[]>) => next(
        offices?.filter(office => {
            if (!showingInactive.value && office.officeStatus === "inactive") {
                return null
            }
            return criterionSurvivesFilter(searchState.value, office)
        })
    ))
    .then("sortedOffices", ({filteredOffices}, next:Link<JSX.Element, Types.Apis.Payloads.Next.Authenticate.Data.Branch[]>) => next(
        filteredOffices?.sort((officeOne, officeTwo) => {
            const nameOne = officeOne['officeName'].toLowerCase()
            const nameTwo = officeTwo['officeName'].toLowerCase()
            const sortedOnBackend = 0

            if (!nameOne) {
                return 1
            }

            if (!nameTwo) {
                return -1
            }
            return sortedOnBackend
        })
    ))
    .then(({
        showingInactive,
        searchState,
        sortedOffices,
        filteredOffices,
        apis,
    }, next:Link<void>) => (
        <div className="office-selector-nrh2o">
            <ReactTooltip  id="office-selector" place="top" delayShow={500}/>
            {!newBranches && <div className="filters-ui">
                <div className="offices-search">
                    <label className="slider-label"><span className="text"
                        xd-qa-type="label" xd-qa-name="Show Inactive">Show Inactive</span>
                        <SliderCheckbox
                            onChange={async () => {
                                await showingInactive.onChange(!showingInactive.value)
                                ReactTooltip.rebuild()
                            }}
                            value={showingInactive.value}
                            xdQaName="Show Inactive Slider"
                        />
                    </label>
                    <div className={`input-with-button`}>
                        <input
                            type="text"
                            value={searchState.value}
                            onChange={(e) => {
                                updateSearch(searchState, e.currentTarget.value)
                            }}
                            placeholder="Search for an office"
                            xd-qa-type="search_field"
                            xd-qa-name="Search for an office"
                        />
                        <button>
                            {
                                !searchState.value &&
                                <FontAwesomeIcon icon={faSearch} />
                            }
                        </button>
                    </div>
                </div>
                <OfficeFilterBar selections={lettersArray} sortedOffices={sortedOffices} viewAllFn={() => updateSearch(searchState, '')}/>
            </div>}
            <div className="offices">
                {filteredOffices.map(office => (
                    <div className="card" key={office?.officeId}
                        id={createIdFromOfficeName(office?.officeName)}
                        xd-qa-type="card"
                        xd-qa-name={office?.officeId}
                    >
                        <div className="title-line">
                            <div className={cx("title", {inactive: office.officeStatus === "inactive"})}
                                xd-qa-type="label"
                                xd-qa-name="Office Name"
                                data-tip={office?.officeName}
                                data-for= "office-selector"
                            >
                                {office?.officeName}
                            </div>
                            {(!(newBranches)) && <div className="login"
                                data-tip={
                                    (!office?.autoActivate && office.officeStatus === "inactive") ?
                                    "Your company has disabled re-activation of inactive offices. Contact your admin for more information." : ""
                                }
                                data-for= "office-selector"
                            >
                                <button
                                    className="primary"
                                    disabled={submitting.value || (!office?.autoActivate && office.officeStatus === "inactive")}
                                    onClick={async () => {
                                        submitting.onChange(true);
                                        try{
                                            const result = await apis.next.authenticate.officeSelection({idOffice: office?.officeId, newUser:false})
                                            handleResponse({result})
                                        } catch (e) {
                                            const result = await e.json()
                                            if(result?.statusCode)
                                            {
                                                handleResponse({result})
                                            }
                                            else {
                                                alert("Something went wrong!")
                                            }
                                    }
                                        submitting.onChange(false);
                                    }}
                                    xd-qa-type="button"
                                    xd-qa-name="Login"
                                >
                                    {office.officeStatus === "inactive" ? (
                                        "Activate & Login"
                                    ) : office.officeStatus === "new" ? (
                                        "Create & Login"
                                    ) : "Login"}
                                </button>
                            </div>}
                        </div>
                        <div className="subTitle"
                            xd-qa-type="label"
                            xd-qa-name="Office Id"
                            data-tip={office?.officeId}
                            data-for= "office-selector"
                        >
                            {office?.officeId}
                        </div>
                        <br/>
                        <div className="office-address-line">
                            <div className="left">
                                <div className={cx("officeAddress", {inactive: !office?.officeStatus})}
                                    xd-qa-type="label"
                                    xd-qa-name="Office Address Line 1"
                                    data-tip={office?.officeAddress1}
                                    data-for= "office-selector"
                                >
                                    {office?.officeAddress1}
                                </div>
                                {office?.officeAddress2 && (
                                    <div
                                        className={cx("officeAddress", {inactive: !office?.officeStatus})}
                                        xd-qa-type="label"
                                        xd-qa-name="Office Address Line 2"
                                        data-tip={office?.officeAddress2}
                                        data-for= "office-selector"
                                    >
                                        {office?.officeAddress2}
                                    </div>
                                )}
                                {office?.officeCity && (
                                    <div
                                        className={cx("officeAddress", {inactive: !office?.officeStatus})}
                                        xd-qa-type="label"
                                        xd-qa-name="Office Address Line 3"
                                        data-tip={`${office?.officeCity}, ${office?.officeState} ${office?.officeZip} ${office?.officeCountry}` }
                                        data-for= "office-selector"
                                    >
                                        {`${office?.officeCity}, ${office?.officeState} ${office?.officeZip} ${office?.officeCountry}` }
                                    </div>
                                )}
                            </div>
                            {(office.officeStatus === "inactive") && <span className="badge inactive" xd-qa-type="label" xd-qa-name="Inactive">Inactive</span>}
                            {(office?.autoActivate && office.officeStatus === "new") && <span className="badge new" xd-qa-type="label" xd-qa-name="New">New</span>}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    ))
    .run()
);


function updateSearch(searchState, value) {
    navigateToSearchStart()
    searchState.onChange(value)
}

const characterArray = () => {
    const characters = [];
    const A_ASCII = 65;
    const Z_ASCII = 90;
    for(let i = A_ASCII; i < Z_ASCII + 1; i++) {
        characters.push(String.fromCharCode(i))
    }

    return characters
}

const lettersArray = characterArray();

const criterionSurvivesFilter = (criterion, office) => {
    const criterionLowerCase = criterion.toLowerCase().trim();
    const searchFieldsValid = [
        'officeName',
        'officeAddress1',
        'officeAddress2',
        'officeCity',
        'officeZip',
        'officeState',
        'officeId'
    ]
    for(let i = 0; i < searchFieldsValid.length; i++) {
        if (office[searchFieldsValid[i]].toLowerCase().includes(criterionLowerCase)) {
            return true
        }
    }
    return false
}
